import { z } from 'zod';
import { Email, IsoDate, IsoDateTime, NonEmptyName, Pence, Phone, Uuid } from '../primitives.js';

/**
 * A client record — the durable, commercially-valuable "book". In a sealed book this row
 * carries `renterId` and is visible ONLY to that renter (not the owner, not other renters);
 * in a shared book `renterId` is null and the row is visible salon-wide. The RLS policy is
 * identical in both cases (ADR-0005); the mode lives in this column, not in the policy.
 */
export const Client = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe('owning renter for a sealed-book client; null = shared'),
    firstName: NonEmptyName,
    lastName: z.string().trim().max(120).nullable(),
    email: Email.nullable(),
    phone: Phone.nullable(),
    notes: z.string().max(4000).nullable(),
    colourFormula: z.string().max(2000).nullable().describe('e.g. "7.3 + 6% · 35 min"'),
    marketingConsent: z.boolean(),
    patchTestLastAt: IsoDate.nullable(),
    patchTestDueAt: IsoDate.nullable(),
    lifetimeValue: Pence.describe('sum of completed-appointment takings'),
    visitCount: z.int().nonnegative(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Client' });
export type Client = z.infer<typeof Client>;

export const CreateClient = z
  .object({
    firstName: NonEmptyName,
    lastName: z.string().trim().max(120).nullish(),
    email: Email.nullish(),
    phone: Phone.nullish(),
    notes: z.string().max(4000).nullish(),
    colourFormula: z.string().max(2000).nullish(),
    marketingConsent: z.boolean().default(false),
    /**
     * Assign to a specific renter's sealed book. If omitted, the server derives it from the
     * acting staff member's book mode (a renter creates sealed clients; an owner creates
     * shared clients). Cross-renter assignment is rejected by RLS.
     */
    renterId: Uuid.nullish(),
  })
  .meta({ id: 'CreateClient' });
export type CreateClient = z.infer<typeof CreateClient>;

export const UpdateClient = z
  .object({
    firstName: NonEmptyName.optional(),
    lastName: z.string().trim().max(120).nullish(),
    email: Email.nullish(),
    phone: Phone.nullish(),
    notes: z.string().max(4000).nullish(),
    colourFormula: z.string().max(2000).nullish(),
    marketingConsent: z.boolean().optional(),
    patchTestLastAt: IsoDate.nullish(),
  })
  .meta({ id: 'UpdateClient' });
export type UpdateClient = z.infer<typeof UpdateClient>;

/**
 * The hard cap on rows per CSV-import request. Sized so the JSON body stays inside the 1 MB
 * app bodyLimit and the per-request tenant transaction stays bounded. The console must split
 * (or refuse) before this is reached; the route enforces it belt-and-braces against this schema.
 */
export const CLIENT_IMPORT_ROW_CAP = 1000;

/**
 * The absolute array ceiling enforced by the body SCHEMA (defence-in-depth against a runaway body
 * blowing the 1 MB bodyLimit). It sits ABOVE {@link CLIENT_IMPORT_ROW_CAP} on purpose: the business
 * cap is enforced IN the route as a 422 `too_many_rows` (a clear, actionable error the console can
 * react to), while anything beyond this hard ceiling is rejected at the edge as a 400 schema error.
 */
export const CLIENT_IMPORT_HARD_MAX = 5000;

/**
 * ONE row of a bulk client CSV import — a deliberately SAFE subset of {@link CreateClient}.
 * It carries NO marketingConsent (GDPR: bulk consent can never be imported without proof, so the
 * server FORCES it false), NO renterId (the book scope is auto-derived from the importer's session),
 * and NO colourFormula (not a CSV-importable field). Invalid rows are collected + skipped, never fatal.
 */
export const ImportClientRow = z
  .object({
    firstName: NonEmptyName,
    lastName: z.string().trim().max(120).nullish(),
    email: Email.nullish(),
    phone: Phone.nullish(),
    notes: z.string().max(4000).nullish(),
  })
  .meta({ id: 'ImportClientRow' });
export type ImportClientRow = z.infer<typeof ImportClientRow>;

/**
 * The PERMISSIVE wire shape of a row as it arrives in the request body. It is intentionally loose
 * (every field optional + nullable) so that an INVALID row (bad email, empty firstName) is NOT
 * rejected at the schema boundary with a 400 that aborts the whole file — instead each row reaches
 * the handler, which re-validates it strictly against {@link ImportClientRow} and collects failures
 * into `invalid[]` per the locked design. The console still constructs strict {@link ImportClientRow}
 * objects, which satisfy this looser contract.
 */
export const ImportClientRowInput = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  notes: z.string().nullish(),
});
export type ImportClientRowInput = z.infer<typeof ImportClientRowInput>;

/**
 * Request body for POST /v1/clients/import — an array of (loosely-typed) rows. The hard array ceiling
 * ({@link CLIENT_IMPORT_HARD_MAX}) is a schema-level body bound; the business cap
 * ({@link CLIENT_IMPORT_ROW_CAP}) is enforced in the route as a 422 `too_many_rows`.
 */
export const ImportClients = z
  .object({
    clients: z
      .array(ImportClientRowInput)
      .max(CLIENT_IMPORT_HARD_MAX, `At most ${CLIENT_IMPORT_HARD_MAX} rows per import`),
  })
  .meta({ id: 'ImportClients' });
export type ImportClients = z.infer<typeof ImportClients>;

/**
 * Result of a bulk client import — HONEST server-side counts the console must surface verbatim.
 * `invalid[].row` and the rows that were de-duplicated are 0-based indices into the SUBMITTED array.
 */
export const ImportClientsResult = z
  .object({
    created: z.int().nonnegative().describe('rows actually inserted'),
    duplicates: z
      .int()
      .nonnegative()
      .describe(
        'rows skipped because a same-email client was already visible (or earlier in the batch)',
      ),
    invalid: z
      .array(z.object({ row: z.int().nonnegative(), reason: z.string() }))
      .describe('rows skipped for validation reasons, with their 0-based index'),
    total: z.int().nonnegative().describe('rows submitted'),
  })
  .meta({ id: 'ImportClientsResult' });
export type ImportClientsResult = z.infer<typeof ImportClientsResult>;

/** GDPR per-client export bundle (Article 20 portability). */
export const ClientExport = z
  .object({
    client: Client,
    appointments: z.array(z.unknown()),
    commsLog: z.array(z.unknown()),
    exportedAt: IsoDateTime,
  })
  .meta({ id: 'ClientExport' });
export type ClientExport = z.infer<typeof ClientExport>;
